import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Mail, Phone, Calendar, Facebook, Instagram, Youtube } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BROKER_PHOTO = "https://customer-assets.emergentagent.com/job_simple-realty-hub/artifacts/c90cntlv_fc0fb51c619dc3497bc8ab60234171d3da217eab-4.jpeg";

const socialIcons = {
  facebook1: Facebook,
  facebook2: Facebook,
  instagram1: Instagram,
  instagram2: Instagram,
  youtube: Youtube,
  tiktok: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
  ),
  calendly: Calendar
};

export const Team = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team`);
      setTeamMembers(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching team:', error);
    } finally {
      setLoading(false);
    }
  };

  const broker = teamMembers.find(m => m.role === 'broker');
  const agents = teamMembers.filter(m => m.role === 'agent');

  return (
    <div data-testid="team-page">
      {/* Hero Section */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="team-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <p className="section-overline">Our Team</p>
            <h1 className="text-4xl sm:text-5xl font-light text-[#F5F5F0] mt-4 mb-6" style={{ fontFamily: 'Playfair Display' }}>
              Meet the <span className="text-gold-gradient">Experts</span>
            </h1>
            <p className="text-lg text-[#808080] max-w-2xl mx-auto">
              Our dedicated team of real estate professionals is committed to providing exceptional service and helping you achieve your real estate goals.
            </p>
          </div>
        </div>
      </section>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="spinner" />
        </div>
      ) : (
        <>
          {/* Broker Section */}
          {broker && (
            <section className="py-16 bg-[#111111]" data-testid="broker-section">
              <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  <div className="flex justify-center">
                    <div className="relative">
                      <img 
                        src={broker.photo_url || BROKER_PHOTO} 
                        alt={broker.name}
                        className="w-80 h-80 object-cover rounded-lg border-2 border-[#D4AF37] gold-glow"
                      />
                      <div className="absolute -bottom-4 -right-4 bg-[#D4AF37] text-[#0a0a0a] px-4 py-2 rounded-lg">
                        <span className="font-semibold text-sm">{broker.title}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="section-overline">Broker / Owner</p>
                    <h2 className="text-3xl font-light text-[#F5F5F0] mt-2 mb-4" style={{ fontFamily: 'Playfair Display' }}>
                      {broker.name}
                    </h2>
                    <p className="text-[#808080] leading-relaxed mb-6">
                      {broker.bio}
                    </p>
                    
                    {broker.specialties && broker.specialties.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-[#F5F5F0] text-sm font-semibold mb-2">Specialties</h4>
                        <div className="flex flex-wrap gap-2">
                          {broker.specialties.map((spec, i) => (
                            <span key={i} className="px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-xs rounded-full">
                              {spec}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="space-y-2 mb-6">
                      {broker.email && (
                        <a href={`mailto:${broker.email}`} className="flex items-center text-[#F5F5F0] hover:text-[#D4AF37] transition-colors">
                          <Mail className="w-4 h-4 mr-2 text-[#D4AF37]" />
                          {broker.email}
                        </a>
                      )}
                      {broker.phone && (
                        <a href={`tel:${broker.phone}`} className="flex items-center text-[#F5F5F0] hover:text-[#D4AF37] transition-colors">
                          <Phone className="w-4 h-4 mr-2 text-[#D4AF37]" />
                          {broker.phone}
                        </a>
                      )}
                    </div>

                    {/* Social Links */}
                    {broker.social_links && Object.keys(broker.social_links).length > 0 && (
                      <div className="flex flex-wrap gap-3 mb-6">
                        {Object.entries(broker.social_links).map(([key, url]) => {
                          const Icon = socialIcons[key] || Calendar;
                          return (
                            <a
                              key={key}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="social-icon"
                              title={key}
                            >
                              <Icon className="w-4 h-4" />
                            </a>
                          );
                        })}
                      </div>
                    )}

                    <a href="https://calendly.com/seantherealtor/15min" target="_blank" rel="noopener noreferrer">
                      <Button className="btn-gold">
                        <Calendar className="w-4 h-4 mr-2" /> Book Consultation
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Agents Section */}
          <section className="py-24 bg-[#0a0a0a]" data-testid="agents-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <div className="text-center mb-12">
                <p className="section-overline">Our Agents</p>
                <h2 className="section-title mt-2">The Team</h2>
              </div>

              {agents.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-[#808080] mb-6">We're growing our team!</p>
                  <Link to="/join">
                    <Button className="btn-gold">
                      Join Our Team
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {agents.map((agent) => (
                    <div key={agent.id} className="team-card" data-testid={`agent-card-${agent.id}`}>
                      <img 
                        src={agent.photo_url || 'https://via.placeholder.com/150'} 
                        alt={agent.name}
                        className="w-32 h-32 rounded-full object-cover mx-auto mb-4 border-2 border-[#D4AF37]"
                      />
                      <h3 className="text-xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
                        {agent.name}
                      </h3>
                      <p className="text-[#D4AF37] text-sm mb-3">{agent.title}</p>
                      <p className="text-[#808080] text-sm mb-4">{agent.bio}</p>
                      <div className="space-y-1 text-sm">
                        {agent.email && (
                          <a href={`mailto:${agent.email}`} className="block text-[#808080] hover:text-[#D4AF37]">
                            {agent.email}
                          </a>
                        )}
                        {agent.phone && (
                          <a href={`tel:${agent.phone}`} className="block text-[#808080] hover:text-[#D4AF37]">
                            {agent.phone}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-[#111111]" data-testid="team-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="section-title mb-4">Want to Join Our Team?</h2>
          <p className="text-[#808080] mb-8 max-w-xl mx-auto">
            We're always looking for talented agents who share our commitment to excellence. Apply today and grow your career with us.
          </p>
          <Link to="/join">
            <Button className="btn-gold">
              Apply Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
